import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  CircleCheck,
  Info,
  XCircle,
} from "@/components/icons";
import { Badge, Skeleton } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { AlertDTO, AlertSeverity } from "@/modules/alerts/domain/types";
import { useAlertsSummaryQuery } from "../api/queries";

const severityStyles: Record<
  AlertSeverity,
  { icon: typeof Info; badge: "default" | "secondary" | "destructive" | "outline" }
> = {
  info: { icon: Info, badge: "secondary" },
  warning: { icon: AlertTriangle, badge: "outline" },
  error: { icon: XCircle, badge: "destructive" },
};

function AlertRow({
  alert,
  embedded = false,
}: {
  alert: AlertDTO;
  embedded?: boolean;
}) {
  const { icon: Icon, badge } = severityStyles[alert.severity];
  const content = (
    <div className="flex items-start gap-3">
      <span
        className={cn(
          "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full",
          alert.severity === "error" && "bg-destructive/10 text-destructive",
          alert.severity === "warning" && "bg-amber-500/10 text-amber-600",
          alert.severity === "info" && "bg-primary/10 text-primary",
        )}
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-sm">{alert.title}</p>
          {alert.count != null && alert.count > 1 ? (
            <Badge variant={badge} className="rounded-full tabular-nums">
              {alert.count}
            </Badge>
          ) : null}
        </div>
        {alert.description ? (
          <p className="text-muted-foreground text-xs">{alert.description}</p>
        ) : null}
      </div>
    </div>
  );

  if (alert.to) {
    return (
      <Link
        to={alert.to}
        className={cn(
          "block transition-colors",
          embedded
            ? "rounded-xl px-2 py-3 hover:bg-muted/40"
            : "brand-card p-4 hover:bg-muted/30",
        )}
      >
        {content}
      </Link>
    );
  }

  return (
    <div
      className={cn(embedded ? "rounded-xl px-2 py-3" : "brand-card p-4")}
    >
      {content}
    </div>
  );
}

type AlertsPanelProps = {
  limit?: number;
  embedded?: boolean;
};

export function AlertsPanel({ limit = 6, embedded = false }: AlertsPanelProps) {
  const summary = useAlertsSummaryQuery();
  const alerts = summary.data?.alerts ?? [];
  const visible = alerts.slice(0, limit);

  if (summary.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-16 rounded-2xl" />
        <Skeleton className="h-16 rounded-2xl" />
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center gap-3",
          embedded
            ? "rounded-xl bg-leaf/5 px-2 py-3"
            : "brand-card border-leaf/25 bg-leaf/5 p-4",
        )}
      >
        <span className="flex size-10 items-center justify-center rounded-xl bg-leaf/15 text-leaf">
          <CircleCheck className="size-5" />
        </span>
        <div>
          <p className="font-medium text-sm">ບໍ່ມີສິ່ງຕ້ອງເຮັດດ່ວນ</p>
          <p className="text-muted-foreground text-xs">
            ລະບົບກວດສອບສະຕັອກ ແລະ ອໍເດີ້ຄ້າງໃໝ່ທຸກ 5 ນາທີ
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col",
        embedded ? "divide-y divide-secondary/10" : "gap-2",
      )}
    >
      {visible.map((alert) => (
        <AlertRow key={alert.id} alert={alert} embedded={embedded} />
      ))}
      {alerts.length > limit ? (
        <p className="px-1 text-muted-foreground text-xs">
          +{alerts.length - limit} ລາຍການເພີ່ມເຕີມ — ເບິ່ງໃນການແຈ້ງເຕືອນ
        </p>
      ) : null}
    </div>
  );
}

export function useOperationalAlertCount(): number {
  const summary = useAlertsSummaryQuery();
  return summary.data?.alerts.length ?? 0;
}
